/**
 * Novo-9 (2026-07-25) — o `tenantId` que sai daqui com id de USUÁRIO do Clerk.
 *
 * MEDIDO AO VIVO: todo `POST /api/v1/chat/stream` da rodada mandou
 * `"tenantId":"user_3CRF426XvWkt5kfUhNuWhglRGrx"`. O mesmo valor viaja no
 * cabeçalho `X-Tenant-Id`.
 *
 * CAUSA, apurada no HAR: NÃO é o `AuthGuard`. O `/api/v1/auth/status` respondeu
 * certo, com `"tenantId":"a0546e6b-5f3b-4d8a-acee-6288c4c44bae"`, e o
 * `authStore` ADOTA esse valor quando a resposta chega. O problema é que existem
 * DUAS cópias da identidade do tenant: a do `authStore`, que é corrigida, e a do
 * `connectionStore`, tirada uma única vez no `initialize()` e nunca mais
 * atualizada. O corpo do `/chat/stream` saía da cópia velha — o placeholder que
 * o `AuthGuard` semeia antes do `/auth/status` responder (`user.id`, escolhido
 * só para satisfazer o guard de "tenant não vazio" do `api.ts`).
 *
 * Ou seja: um valor de identidade duplicado, onde só uma das cópias aprende.
 *
 * A REGRA: o que SAI daqui vem da fonte autoritativa, lida na hora do envio —
 * nunca de um retrato tirado antes de a resposta do servidor existir. É o mesmo
 * padrão que o guard anti-cross-tenant do `connectionStore` já usava
 * (`useAuthStore.getState().tenantId`), agora aplicado também ao que é enviado,
 * e não só ao que é recebido.
 *
 * PURO: recebe os dois valores e decide. Sem store, sem I/O.
 */

/** UUID canônico (8-4-4-4-12) — a forma de um tenant de verdade. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `true` quando o valor tem forma de tenant. */
export function isTenantUuid(value: string | null | undefined): boolean {
  return typeof value === 'string' && UUID_RE.test(value.trim())
}

/**
 * O `tenantId` que deve viajar numa requisição de saída.
 *
 * Ordem: o do `authStore` (corrigido pelo `/auth/status`) quando tem forma de
 * tenant; senão o do `connectionStore`, se tiver; senão o do `authStore` como
 * está. A última linha existe porque o `api.ts` recusa tenant vazio, e um
 * placeholder que o motor ignora ainda é melhor do que uma requisição que nem
 * sai — mas ele só é alcançado antes de o `/auth/status` responder, quando o
 * `AuthGuard` ainda nem liberou a tela de chat.
 */
export function resolveOutboundTenantId(
  authStoreTenantId: string | null | undefined,
  connectionStoreTenantId: string | null | undefined,
): string {
  if (isTenantUuid(authStoreTenantId)) return (authStoreTenantId as string).trim()
  if (isTenantUuid(connectionStoreTenantId)) return (connectionStoreTenantId as string).trim()
  return (authStoreTenantId ?? connectionStoreTenantId ?? '').trim()
}
